import { API_BASE_URL } from '../api';
import { authService } from './authService';
import type {
  BlogArticle,
  BlogArticleListItem,
  BlogArticleCreate,
  BlogArticleUpdate,
} from '../types/blog';

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let detail = text;
    try {
      const json = JSON.parse(text);
      detail = json.detail || text;
    } catch {
      /* keep raw text */
    }
    throw new Error(detail);
  }
  return res.json();
}

/**
 * Résout une URL d'image renvoyée par l'API (`/api/uploads/...`) en URL absolue
 * vers le backend, ou laisse intacte une URL déjà absolue (http...).
 */
export function resolveImageUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

/**
 * GET public tolérant aux défaillances transitoires du premier appel d'une
 * session : backend qui « démarre à froid », réponse (y compris 404) mise en
 * cache trop tôt par Cloudflare/navigateur, etc. On retente quelques fois avec
 * un léger backoff ; dès la 2ᵉ tentative on force une requête fraîche
 * (`cache: 'no-store'` + paramètre anti-cache) pour court-circuiter tout cache
 * amont — c'est l'équivalent programmatique du F5 que faisait l'utilisateur.
 * Un timeout par tentative (6 s) évite de rester bloqué sur une connexion
 * pendante et borne l'attente avant de conclure (pire cas ~ attempts × 6 s).
 */
const PUBLIC_FETCH_TIMEOUT_MS = 6000;

async function getPublicJson<T>(path: string, attempts = 3): Promise<T> {
  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, 500 * i));
    const fresh = i > 0;
    const url = `${API_BASE_URL}${path}${fresh ? `${path.includes('?') ? '&' : '?'}_=${Date.now()}` : ''}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PUBLIC_FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        signal: controller.signal,
        ...(fresh ? { cache: 'no-store' as RequestCache } : {}),
      });
      return await handleResponse<T>(res);
    } catch (e) {
      lastError = e;
    } finally {
      clearTimeout(timeout);
    }
  }
  throw lastError;
}

export const blogService = {
  // ====== PUBLIC ======
  async listPublished(): Promise<BlogArticleListItem[]> {
    return getPublicJson<BlogArticleListItem[]>('/api/blog/articles');
  },

  async getBySlug(slug: string): Promise<BlogArticle> {
    return getPublicJson<BlogArticle>(`/api/blog/articles/${slug}`);
  },

  // ====== BACKOFFICE ======
  async listAll(): Promise<BlogArticleListItem[]> {
    const headers = await authService.getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/articles`, { headers });
    return handleResponse<BlogArticleListItem[]>(res);
  },

  async getById(id: number): Promise<BlogArticle> {
    const headers = await authService.getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/articles/${id}`, { headers });
    return handleResponse<BlogArticle>(res);
  },

  async create(data: BlogArticleCreate): Promise<BlogArticle> {
    const headers = await authService.getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/articles`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return handleResponse<BlogArticle>(res);
  },

  async update(id: number, data: BlogArticleUpdate): Promise<BlogArticle> {
    const headers = await authService.getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/articles/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    return handleResponse<BlogArticle>(res);
  },

  async delete(id: number): Promise<void> {
    const headers = await authService.getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/articles/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text);
    }
  },

  async uploadImage(file: File): Promise<string> {
    const headers = await authService.getAuthHeaders();
    // Retire Content-Type pour laisser le navigateur fixer le boundary multipart
    const { 'Content-Type': _omit, ...uploadHeaders } = headers as Record<string, string>;
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_BASE_URL}/api/backoffice/blog/upload-image`, {
      method: 'POST',
      headers: uploadHeaders,
      body: formData,
    });
    const data = await handleResponse<{ image_url: string }>(res);
    return data.image_url;
  },
};
